/**
 * My Connections page — S7 (consumer dashboard).
 *
 * History:
 * - S2: 48-line stub.
 * - S6: paired the USER list with the integration catalog so users
 *   could initiate connections outside a step (RFC §4.2).
 * - S7 (this revision): added two sections to satisfy the full §4.6
 *   consumer story:
 *     1. **Run-scoped (ephemeral)** — surfaces RUN-tier connections
 *        the user picked up via JIT Connect. They auto-expire (the
 *        S5 sweeper handles teardown), but until then the user
 *        deserves a place to see what's live and revoke early if
 *        they change their mind.
 *     2. **Inactive** — collapsible audit trail of REVOKED /
 *        EXPIRED connections so a consumer can answer "did I
 *        actually disconnect that?" without spelunking the org
 *        audit (which they can't see anyway).
 *
 * The `MyConnectionsPage.tsx` rename called out in the eng plan was
 * intentionally skipped — the route already lives at
 * `/app/settings/connections` and renaming the file just churns the
 * import graph. This file IS the My Connections page.
 */

import React, {useState} from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  Collapse,
  Button,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {Link as RouterLink} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {ConnectionList, IntegrationCatalogGrid} from '../components/integrations';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {GET_MY_CONNECTIONS} from '../graphql/queries';
import {
  ConnectionStatus,
  ConnectionTier,
  type IntegrationConnection,
} from '../types/generated/graphql';

interface MyConnectionsQueryResult {
  myConnections: IntegrationConnection[];
}

export default function PersonalConnectionsPage() {
  useDocumentTitle('My Connections');
  const [showInactive, setShowInactive] = useState(false);

  const {data} = useQuery<MyConnectionsQueryResult>(GET_MY_CONNECTIONS, {
    fetchPolicy: 'cache-and-network',
  });

  const allConnections = data?.myConnections ?? [];

  // Catalog should hide every USER integration the user has actively
  // wired up — but NOT hide ones they revoked (revoking and
  // reconnecting is a legit recovery flow, e.g. after a token leak).
  const connectedUserIntegrationIds = allConnections
    .filter(
      (c) =>
        c.tier === ConnectionTier.User &&
        c.status !== ConnectionStatus.Disconnected &&
        c.status !== ConnectionStatus.Revoked,
    )
    .map((c) => c.integrationId);

  const runCount = allConnections.filter(
    (c) =>
      c.tier === ConnectionTier.Run &&
      c.status !== ConnectionStatus.Disconnected &&
      c.status !== ConnectionStatus.Revoked,
  ).length;

  const inactiveCount = allConnections.filter(
    (c) =>
      c.status === ConnectionStatus.Revoked ||
      c.status === ConnectionStatus.Disconnected,
  ).length;

  return (
    <Box sx={{flex: 1, bgcolor: 'background.default'}}>
      <Container maxWidth="lg" sx={{py: 4}}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{mb: 2}}
          aria-label="breadcrumb"
        >
          <MuiLink
            component={RouterLink}
            to="/app/settings"
            underline="hover"
            color="inherit"
          >
            Settings
          </MuiLink>
          <Typography color="text.primary">Connections</Typography>
        </Breadcrumbs>
        <Typography variant="h5" sx={{mb: 1, fontWeight: 600}}>
          My Connections
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          Manage your personal integration accounts. These connections are
          only used for steps assigned to you.
        </Typography>

        <Paper variant="outlined" sx={{p: 3, mb: 3}}>
          <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
            Personal Connections
          </Typography>
          <ConnectionList
            tierFilter={ConnectionTier.User}
            statusFilter="ACTIVE_ONLY"
            showMetadata
            emptyMessage="No personal connections yet. Pick one from the catalog below or wait for a step to prompt you."
          />
        </Paper>

        {runCount > 0 && (
          <Paper variant="outlined" sx={{p: 3, mb: 3}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 0.5}}>
              Run-scoped (ephemeral)
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{display: 'block', mb: 2}}
            >
              These connections were created for a single run and will be
              automatically revoked when the run finishes (or sooner if you
              revoke them here). One-shot integrations like Plaid live here.
            </Typography>
            <ConnectionList
              tierFilter={ConnectionTier.Run}
              statusFilter="ACTIVE_ONLY"
              showMetadata
              enableRunPromotion
              emptyMessage="No run-scoped connections."
            />
          </Paper>
        )}

        <Divider sx={{my: 3}} />

        <Paper variant="outlined" sx={{p: 3, mb: 3}}>
          <IntegrationCatalogGrid
            tier={ConnectionTier.User}
            connectedIntegrationIds={connectedUserIntegrationIds}
            title="Available integrations"
          />
        </Paper>

        {inactiveCount > 0 && (
          <Paper variant="outlined" sx={{p: 3, mb: 3}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                  Inactive ({inactiveCount})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Connections you've revoked or that have expired. Kept for
                  audit; they can't be reactivated — reconnect from the
                  catalog instead.
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setShowInactive((v) => !v)}
                startIcon={showInactive ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {showInactive ? 'Hide' : 'Show'}
              </Button>
            </Box>
            <Collapse in={showInactive} unmountOnExit>
              <Box sx={{mt: 2}}>
                <ConnectionList
                  tierFilter={ConnectionTier.User}
                  statusFilter="TERMINAL_ONLY"
                  showMetadata
                  readonly
                  emptyMessage="No inactive personal connections."
                />
                <ConnectionList
                  tierFilter={ConnectionTier.Run}
                  statusFilter="TERMINAL_ONLY"
                  showMetadata
                  readonly
                  emptyMessage=""
                />
              </Box>
            </Collapse>
          </Paper>
        )}

        <Typography variant="caption" color="text.secondary">
          Your personal connections are private and not shared with your
          organization. Use org integrations for shared tools like HubSpot or
          Monday.
        </Typography>
      </Container>
    </Box>
  );
}
